import React from 'react';
import { TSPEvent } from '@/types/tsp';
import { TeamScoreControl } from './TeamScoreControl';
import { InningControl } from './InningControl';
import { BallsStrikesControl } from './BallsStrikesControl';
import { OutsControl } from './OutsControl';
import { BaseRunnersControl } from './BaseRunnersControl';
import { HitsErrorsControl } from './HitsErrorsControl';

interface ScoreboardControlsProps {
  teamAScore: number;
  teamBScore: number;
  teamAHits: number;
  teamAErrors: number;
  teamBHits: number;
  teamBErrors: number;
  balls: number;
  strikes: number;
  outs: number;
  count: number;
  isTopInning: boolean;
  baseRunners: boolean[];
  selectedGame?: TSPEvent;
  onTeamAScoreChange: (score: number) => void;
  onTeamBScoreChange: (score: number) => void;
  onTeamAHitsChange: (hits: number) => void;
  onTeamAErrorsChange: (errors: number) => void;
  onTeamBHitsChange: (hits: number) => void;
  onTeamBErrorsChange: (errors: number) => void;
  onBallsChange: (balls: number) => void;
  onStrikesChange: (strikes: number) => void;
  onOutsChange: (outs: number) => void;
  onCountChange: (count: number) => void;
  onInningHalfChange: (isTop: boolean) => void;
  onResetCount: () => void;
  onBaseRunnerChange: (index: number) => void;
}

export function ScoreboardControls({
  teamAScore,
  teamBScore,
  teamAHits,
  teamAErrors,
  teamBHits,
  teamBErrors,
  balls,
  strikes,
  outs,
  count,
  isTopInning,
  baseRunners,
  selectedGame,
  onTeamAScoreChange,
  onTeamBScoreChange,
  onTeamAHitsChange,
  onTeamAErrorsChange,
  onTeamBHitsChange,
  onTeamBErrorsChange,
  onBallsChange,
  onStrikesChange,
  onOutsChange,
  onCountChange,
  onInningHalfChange,
  onResetCount,
  onBaseRunnerChange,
}: ScoreboardControlsProps) {
  const teamAName = selectedGame ? selectedGame.meta.event_away_team_abbreviation : 'Team A';
  const teamBName = selectedGame ? selectedGame.meta.event_home_team_abbreviation : 'Team B';

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      {/* Team Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TeamScoreControl
          teamName={teamAName}
          score={teamAScore}
          onScoreChange={onTeamAScoreChange}
        />
        <TeamScoreControl
          teamName={teamBName}
          score={teamBScore}
          onScoreChange={onTeamBScoreChange}
        />
      </div>

      {/* Hits & Errors Controls */}
      <HitsErrorsControl
        teamAHits={teamAHits}
        teamAErrors={teamAErrors}
        teamBHits={teamBHits}
        teamBErrors={teamBErrors}
        teamAName={teamAName}
        teamBName={teamBName}
        onTeamAHitsChange={onTeamAHitsChange}
        onTeamAErrorsChange={onTeamAErrorsChange}
        onTeamBHitsChange={onTeamBHitsChange}
        onTeamBErrorsChange={onTeamBErrorsChange}
      />

      {/* Game Status Controls */}
      <div className="bg-[#1A1F2C]/80 rounded-lg p-4 backdrop-blur-sm border border-white/10">
        <h3 className="text-xl font-semibold text-white mb-4">Game Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <InningControl
            count={count}
            isTopInning={isTopInning}
            onCountChange={onCountChange}
            onInningHalfChange={onInningHalfChange}
          />
          <BallsStrikesControl
            balls={balls}
            strikes={strikes}
            onResetCount={onResetCount}
            onBallsChange={onBallsChange}
            onStrikesChange={onStrikesChange}
          />
          <OutsControl
            outs={outs}
            onOutsChange={onOutsChange}
          />
        </div>
        <div className="mt-4">
          <BaseRunnersControl
            baseRunners={baseRunners}
            onBaseRunnerChange={onBaseRunnerChange}
          />
        </div>
      </div>
    </div>
  );
}
